import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { brokerOutreachApi, type BrokerOutreachCardDto } from "@/api/brokerOutreach";
import { Button, Textarea, useToast } from "@/design-system/components/ui";
import { cn } from "@/design-system/lib/utils";
import { useSendHistory } from "@/hooks/buyers/useSendHistory";
import { CollapsibleCard } from "../introCards/CollapsibleCard";
import { HeaderAction } from "../introCards/HeaderAction";
import { IntroCardPill } from "../introCards/IntroCardPill";
import { draftNewerThanSend, isDone, latestVersion, outreachCardStatus } from "../introCards/introCardStatus";
import { EditAndSendAgain, SentFold } from "../introCards/SentFold";
import { countSms } from "../outreach/gsm7";
import { writeClipboard } from "../outreach/outreachCopy";
import { brokerOutreachCopy } from "./brokerOutreachCopy";
import { CardSentLine } from "./CardStatusLine";
import { useInvalidateWithOutreachState } from "./useBrokerOutreach";

/**
 * The Text card (D27) and the follow-up text (D33) — one component, because
 * they are the same act: an SMS the consultant sends from their OWN phone.
 *
 * <p>🔴 <b>Phoenix sends nothing.</b> DRAFT asks the assistant for a message
 * written from code-computed facts (the server guards it and ships a fallback
 * template when it fails); the consultant edits freely — their edits are never
 * gated — then COPY puts the text on the clipboard and LOG records exactly what
 * was copied: one send version and one communications entry on the buyer, the
 * submission and the listing. Copying writes nothing.</p>
 *
 * <p>The counter is the carrier's arithmetic (`countSms`, shared with the
 * Outreach tab and not forked): characters and SMS segments. The ~320-character
 * target is a guide, never a block (D27).</p>
 *
 * <p>No role statement (R8): a text goes out as the consultant wrote it.</p>
 *
 * <p><b>A collapsible card (D2).</b> The tab decides whether it is open; the
 * typed text and the copied flag live HERE, above the hidden body, so a
 * collapse loses neither and the collapsed header's action (J5) reads the same
 * text the body's COPY would. Once logged, the latest text that went out is
 * folded at the top and the composer waits behind "Edit and send again" — unless
 * the saved draft is newer than that send, which is unsent work (J8).</p>
 */
export function BrokerTextCard({
  submissionId,
  channel,
  title,
  card,
  phone,
  targetCharacters,
  canWrite,
  testId,
  open,
  onOpenChange,
  onActed,
}: {
  submissionId: string;
  channel: "text" | "follow-up-text";
  title: string;
  card: BrokerOutreachCardDto;
  phone: string | null;
  targetCharacters: number;
  canWrite: boolean;
  testId: string;
  open: boolean;
  onOpenChange: (next: boolean) => void;
  /** Called after a LOG this tab performed succeeds (J6). */
  onActed?: () => void;
}) {
  const toast = useToast();
  const draft = card.draft;
  const [copied, setCopied] = useState<string | null>(null);
  const [revealed, setRevealed] = useState(false);
  const invalidateWithState = useInvalidateWithOutreachState(submissionId);
  const history = useSendHistory(submissionId);

  // The typed text, reseeded whenever a (re)draft lands — what keying the
  // composer on `generatedAtUtc` did when the text lived inside it.
  const saved = draft?.body ?? "";
  const [seededFrom, setSeededFrom] = useState(draft?.generatedAtUtc ?? null);
  const [text, setText] = useState(saved);
  if ((draft?.generatedAtUtc ?? null) !== seededFrom) {
    setSeededFrom(draft?.generatedAtUtc ?? null);
    setText(saved);
  }
  const empty = text.trim().length === 0;

  const generate = useMutation({
    mutationFn: () => brokerOutreachApi.draft(submissionId, channel),
    onSuccess: (s) => {
      invalidateWithState(s);
      setCopied(null);
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const log = useMutation({
    mutationFn: (body: string) => brokerOutreachApi.log(submissionId, channel, { body }),
    onSuccess: (s) => {
      // The entry lands on three Communications feeds (buyer, submission,
      // listing) — refresh them all.
      invalidateWithState(s, { logged: true });
      setCopied(null);
      setRevealed(false);
      toast.success("Logged. It is on the buyer's, the submission's and the listing's Communications.");
      onActed?.();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const copy = async () => {
    const value = text.trim();
    if (await writeClipboard(value)) {
      setCopied(value);
      toast.success("Text copied.");
    } else {
      toast.error("Couldn't copy the text. Select it and copy by hand.");
    }
  };

  const status = outreachCardStatus(card, copied != null);
  const done = isDone(status);
  const latest = latestVersion(
    channel === "text" ? history.data?.brokerTexts : history.data?.brokerFollowUpTexts,
  );
  const unsentDraft = draft != null && draftNewerThanSend([draft.generatedAtUtc, draft.editedAtUtc], card.sentAtUtc);
  // J8 — writers only: a reader keeps today's read-only view, with no control to reach.
  // The editor area hides by CLASS as well as the attribute: Tailwind's preflight
  // `[hidden]` rule loses to `.grid` (same specificity, emitted earlier).
  const editorHidden = canWrite && done && !revealed && !unsentDraft;

  const action = !canWrite ? null : status.state === "notDrafted" ? (
    <HeaderAction
      label={generate.isPending ? "Drafting…" : "Draft"}
      onClick={() => generate.mutate()}
      disabled={!card.available || generate.isPending}
      reason={card.unavailableReason}
      testId={`${testId}-header-draft`}
    />
  ) : status.state === "copied" ? (
    <HeaderAction
      label={log.isPending ? "Logging…" : "Log"}
      onClick={() => copied && log.mutate(copied)}
      disabled={log.isPending || !card.available}
      reason={card.unavailableReason}
      testId={`${testId}-header-log`}
    />
  ) : status.state === "draftSaved" ? (
    <HeaderAction
      label="Copy"
      onClick={() => void copy()}
      disabled={empty || !card.available}
      reason={card.unavailableReason}
      testId={`${testId}-header-copy`}
    />
  ) : null;

  return (
    <CollapsibleCard
      open={open}
      onOpenChange={onOpenChange}
      title={title}
      recipient="To the listing broker"
      pill={<IntroCardPill status={status} logged />}
      action={action}
      testId={testId}
    >
      <div className="grid gap-space-3">
        {done ? <CardSentLine card={card} testId={`${testId}-sent-line`} /> : null}
        {done && latest ? <SentFold body={latest.body} testId={`${testId}-sent`} /> : null}

        <div className="flex flex-wrap items-center justify-between gap-space-2">
          <span className="text-small text-onyx min-w-0 break-words" data-testid={`${testId}-phone`}>
            {phone ?? <span className="text-gray-mid">No phone on file</span>}
          </span>
          {phone ? (
            <Button
              variant="text"
              size="sm"
              onClick={() =>
                void writeClipboard(phone).then((ok) =>
                  ok ? toast.success("Number copied.") : toast.error("Couldn't copy the number. Select it and copy by hand."),
                )
              }
            >
              Copy number
            </Button>
          ) : null}
        </div>

        {!card.available ? (
          // D25: no card is locked; one that cannot be used says why.
          <p className="text-small text-status-warning m-0" data-testid={`${testId}-unavailable`}>
            {card.unavailableReason}
          </p>
        ) : card.hint ? (
          <p className="text-small text-status-warning-ink m-0" data-testid={`${testId}-hint`}>
            {card.hint}
          </p>
        ) : null}

        {editorHidden && canWrite ? (
          <EditAndSendAgain onClick={() => setRevealed(true)} testId={`${testId}-edit-again`} />
        ) : null}

        <div hidden={editorHidden} className={cn("grid gap-space-3", editorHidden && "hidden")} data-testid={`${testId}-editor`}>
          {!draft ? (
            canWrite ? (
              <div>
                <Button
                  variant="secondary"
                  onClick={() => generate.mutate()}
                  disabled={!card.available || generate.isPending}
                  data-testid={`${testId}-draft`}
                >
                  {generate.isPending ? "Drafting…" : "Draft"}
                </Button>
              </div>
            ) : (
              <p className="text-small text-gray-mid m-0">Nothing drafted yet.</p>
            )
          ) : (
            <TextComposer
              submissionId={submissionId}
              channel={channel}
              card={card}
              text={text}
              onTextChange={setText}
              targetCharacters={targetCharacters}
              canWrite={canWrite}
              testId={testId}
              copied={copied}
              onCopy={() => void copy()}
              onRedraft={() => generate.mutate()}
              redrafting={generate.isPending}
              onLog={(body) => log.mutate(body)}
              logging={log.isPending}
            />
          )}
        </div>
      </div>
    </CollapsibleCard>
  );
}

function TextComposer({
  submissionId,
  channel,
  card,
  text,
  onTextChange,
  targetCharacters,
  canWrite,
  testId,
  copied,
  onCopy,
  onRedraft,
  redrafting,
  onLog,
  logging,
}: {
  submissionId: string;
  channel: "text" | "follow-up-text";
  card: BrokerOutreachCardDto;
  text: string;
  onTextChange: (next: string) => void;
  targetCharacters: number;
  canWrite: boolean;
  testId: string;
  copied: string | null;
  onCopy: () => void;
  onRedraft: () => void;
  redrafting: boolean;
  onLog: (body: string) => void;
  logging: boolean;
}) {
  const toast = useToast();
  const invalidateWithState = useInvalidateWithOutreachState(submissionId);
  const saved = card.draft?.body ?? "";
  const sms = countSms(text);
  const dirty = text.trim() !== saved.trim();
  const empty = text.trim().length === 0;

  const save = useMutation({
    mutationFn: () => brokerOutreachApi.saveDraft(submissionId, channel, text),
    onSuccess: (s) => {
      invalidateWithState(s);
      toast.success("Draft saved.");
    },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <div className="grid gap-space-3">
      <Textarea
        rows={5}
        value={text}
        readOnly={!canWrite}
        aria-label={`${channel === "text" ? "Text" : "Follow-up text"} message`}
        data-testid={`${testId}-body`}
        onChange={(e) => onTextChange(e.target.value)}
      />
      <div className="flex flex-wrap items-center justify-between gap-space-2">
        <span className="text-caption text-gray-mid" data-testid={`${testId}-count`}>
          {sms.characters} {sms.characters === 1 ? "character" : "characters"} · {sms.segments} SMS{" "}
          {sms.segments === 1 ? "segment" : "segments"}
        </span>
        <span
          className={`text-caption ${sms.characters > targetCharacters ? "text-status-warning-ink" : "text-gray-mid"}`}
          data-testid={`${testId}-target`}
        >
          {brokerOutreachCopy.textTarget(targetCharacters)}
        </span>
      </div>
      <p className="text-caption text-gray-mid m-0">
        {card.draft?.fromTemplate
          ? "Written from the fallback template — the assistant's draft didn't pass the checks or wasn't available."
          : "Drafted by the assistant from the facts on file. Read it through and change anything."}{" "}
        {brokerOutreachCopy.noRoleStatement}
      </p>

      {canWrite ? (
        <>
          <div className="flex flex-wrap items-center gap-space-3">
            <Button
              onClick={onCopy}
              disabled={empty || !card.available}
              data-testid={`${testId}-copy`}
            >
              Copy
            </Button>
            <Button
              variant="secondary"
              onClick={() => copied && onLog(copied)}
              disabled={!copied || logging || !card.available}
              data-testid={`${testId}-log`}
            >
              {logging ? "Logging…" : "Log"}
            </Button>
            <Button
              variant="text"
              onClick={() => save.mutate()}
              disabled={!dirty || empty || save.isPending}
            >
              {save.isPending ? "Saving…" : "Save draft"}
            </Button>
            <Button variant="text" onClick={onRedraft} disabled={redrafting || !card.available}>
              {redrafting ? "Redrafting…" : "Redraft"}
            </Button>
          </div>
          {copied ? (
            <p role="status" className="text-small text-gray-mid m-0" data-testid={`${testId}-copied`}>
              {brokerOutreachCopy.copiedLog}
            </p>
          ) : null}
        </>
      ) : (
        <p className="text-caption text-gray-mid m-0">Only this buyer&apos;s consultant can copy and log it.</p>
      )}
    </div>
  );
}
